export type ShortcutCategory = 'page' | 'navigation' | 'editor';

export type ShortcutDefinition = {
    keys: string;
    eventKey?: string;
    i18nKey: string;
    display?: string;
    // Listed in the reference but bound elsewhere, so it cannot be passed to `useShortcut`.
    referenceOnly?: true;
};

/*
 * t('shortcuts.action.save')
 * t('shortcuts.action.cancel')
 * t('shortcuts.action.showShortcuts')
 * t('shortcuts.action.switchTabContent')
 * t('shortcuts.action.switchTabPublication')
 * t('shortcuts.action.switchTabPush')
 * t('shortcuts.action.metadataHistory')
 * t('shortcuts.action.placementHistory')
 * t('shortcuts.action.preview')
 * t('shortcuts.action.focusPublicationStatus')
 * t('shortcuts.action.focusContentStatus')
 * t('shortcuts.action.cycleRegion')
 * t('shortcuts.action.openMetadataFromOverview')
 * t('shortcuts.action.bold')
 * t('shortcuts.action.italic')
 * t('shortcuts.action.underline')
 * t('shortcuts.action.superscript')
 * t('shortcuts.action.subscript')
 * t('shortcuts.action.unorderedList')
 * t('shortcuts.action.orderedList')
 * t('shortcuts.action.link')
 * t('shortcuts.action.moveElementUp')
 * t('shortcuts.action.moveElementDown')
 * t('shortcuts.action.rephrase')
 * t('shortcuts.action.openComments')
 * t('shortcuts.action.openSpellChecker')
 * t('shortcuts.action.openInsertMenu')
 * t('shortcuts.action.insertMenuSwitchTab')
 * t('shortcuts.action.insertMenuSwitchColumn')
 */

const PAGE_SHORTCUTS = {
    save: {
        keys: 'mod+s',
        i18nKey: 'shortcuts.action.save',
    },
    cancel: {
        keys: 'mod+shift+x',
        i18nKey: 'shortcuts.action.cancel',
    },
    showShortcuts: {
        keys: 'shift+/',
        eventKey: '?',
        display: '?',
        i18nKey: 'shortcuts.action.showShortcuts',
    },
} as const satisfies Record<string, ShortcutDefinition>;

const NAVIGATION_SHORTCUTS = {
    switchTabContent: {
        keys: 'mod+shift+0',
        i18nKey: 'shortcuts.action.switchTabContent',
    },
    switchTabPublication: {
        keys: 'mod+shift+1',
        i18nKey: 'shortcuts.action.switchTabPublication',
    },
    switchTabPush: {
        keys: 'mod+shift+2',
        i18nKey: 'shortcuts.action.switchTabPush',
    },
    metadataHistory: {
        keys: 'mod+shift+6',
        i18nKey: 'shortcuts.action.metadataHistory',
    },
    placementHistory: {
        keys: 'mod+shift+7',
        i18nKey: 'shortcuts.action.placementHistory',
    },
    preview: {
        keys: 'mod+shift+8',
        i18nKey: 'shortcuts.action.preview',
    },
    focusPublicationStatus: {
        keys: 'mod+shift+s',
        i18nKey: 'shortcuts.action.focusPublicationStatus',
    },
    focusContentStatus: {
        keys: 'mod+shift+l',
        i18nKey: 'shortcuts.action.focusContentStatus',
    },
    cycleRegion: {
        keys: 'mod+shift+9',
        i18nKey: 'shortcuts.action.cycleRegion',
    },
    openMetadataFromOverview: {
        keys: 'alt+click',
        i18nKey: 'shortcuts.action.openMetadataFromOverview',
        referenceOnly: true,
    },
} as const satisfies Record<string, ShortcutDefinition>;

const EDITOR_SHORTCUTS = {
    bold: {
        keys: 'mod+b',
        i18nKey: 'shortcuts.action.bold',
    },
    italic: {
        keys: 'mod+i',
        i18nKey: 'shortcuts.action.italic',
    },
    underline: {
        keys: 'mod+u',
        i18nKey: 'shortcuts.action.underline',
    },
    superscript: {
        keys: 'mod+h',
        i18nKey: 'shortcuts.action.superscript',
    },
    subscript: {
        keys: 'mod+g',
        i18nKey: 'shortcuts.action.subscript',
    },
    unorderedList: {
        keys: 'alt+u',
        i18nKey: 'shortcuts.action.unorderedList',
    },
    orderedList: {
        keys: 'alt+o',
        i18nKey: 'shortcuts.action.orderedList',
    },
    link: {
        keys: 'mod+k',
        i18nKey: 'shortcuts.action.link',
    },
    moveElementUp: {
        keys: 'mod+shift+up',
        i18nKey: 'shortcuts.action.moveElementUp',
    },
    moveElementDown: {
        keys: 'mod+shift+down',
        i18nKey: 'shortcuts.action.moveElementDown',
    },
    rephrase: {
        keys: 'mod+j',
        i18nKey: 'shortcuts.action.rephrase',
    },
    // `ctrl` matches `ctrlKey` on all platforms.
    openComments: {
        keys: 'ctrl+shift+k',
        i18nKey: 'shortcuts.action.openComments',
        referenceOnly: true,
    },
    openSpellChecker: {
        keys: 'ctrl+shift+a',
        i18nKey: 'shortcuts.action.openSpellChecker',
        referenceOnly: true,
    },
    openInsertMenu: {
        keys: 'mod+e',
        i18nKey: 'shortcuts.action.openInsertMenu',
    },
    insertMenuSwitchTab: {
        keys: 'mod+arrows',
        i18nKey: 'shortcuts.action.insertMenuSwitchTab',
        referenceOnly: true,
    },
    insertMenuSwitchColumn: {
        keys: 'arrows',
        i18nKey: 'shortcuts.action.insertMenuSwitchColumn',
        referenceOnly: true,
    },
} as const satisfies Record<string, ShortcutDefinition>;

export const SHORTCUTS = {
    ...PAGE_SHORTCUTS,
    ...NAVIGATION_SHORTCUTS,
    ...EDITOR_SHORTCUTS,
};

export type ShortcutId = keyof typeof SHORTCUTS;

export type BindableShortcutId = {
    [Id in ShortcutId]: (typeof SHORTCUTS)[Id] extends { referenceOnly: true } ? never : Id;
}[ShortcutId];

const SHORTCUT_GROUPS = [
    { category: 'navigation', shortcuts: NAVIGATION_SHORTCUTS },
    { category: 'editor', shortcuts: EDITOR_SHORTCUTS },
    { category: 'page', shortcuts: PAGE_SHORTCUTS },
] as const;

export const getShortcutsByCategory = (): {
    category: ShortcutCategory;
    shortcuts: { id: ShortcutId; definition: ShortcutDefinition }[];
}[] =>
    SHORTCUT_GROUPS.map(({ category, shortcuts }) => ({
        category,
        shortcuts: (Object.keys(shortcuts) as ShortcutId[]).map((id) => ({
            id,
            definition: SHORTCUTS[id],
        })),
    }));
